import { SquaresFourIcon } from "@phosphor-icons/react/dist/csr/SquaresFour";
import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import * as React from "react";
import { Button } from "@volca/ui/components/ui/button";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import { PageHeader } from "@volca/ui/components/ui/page-components";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import {
  TimeframeFilter,
  TimeframeValue,
} from "@volca/ui/components/ui/time-frame-filter";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import { cn } from "@volca/ui/lib/utils";

type ToggleSwitchProps = {
  value: string;
  onValueChange(value: string): void;
  className?: string;
  children: React.ReactNode;
};

const ToggleSwitch = ({
  className,
  value,
  onValueChange,
  children,
}: ToggleSwitchProps) => (
  <ToggleGroup
    value={[value]}
    onValueChange={(groupValue) => {
      const next = groupValue[0];
      if (next !== undefined && next !== value) onValueChange(next);
    }}
    spacing={1}
    className={cn("rounded-full bg-neutral p-1", className)}
  >
    {children}
  </ToggleGroup>
);

const ToggleSwitchItem = ({
  className,
  value,
  children,
  ...props
}: Omit<React.ComponentProps<typeof ToggleGroupItem>, "className"> & {
  className?: string;
  children: React.ReactNode;
}) => (
  <ToggleGroupItem
    value={value}
    data-value={value}
    className={cn(
      "rounded-full text-subtle hover:bg-transparent hover:text-ink",
      "aria-pressed:bg-surface aria-pressed:text-ink aria-pressed:shadow-raised",
      className,
    )}
    {...props}
  >
    {children}
  </ToggleGroupItem>
);

interface ReferralsHeaderProps {
  referralsCount: number;
  isLoadingReferrals: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  timeframeValue: TimeframeValue;
  onTimeframeChange: (value: TimeframeValue) => void;
  viewType: "kanban" | "table";
  onViewChange: (view: "kanban" | "table") => void;
  onAddAdvocate: () => void;
  onAddReferral: () => void;
  noReferrals: boolean;
}

export const ReferralsHeader = ({
  referralsCount,
  isLoadingReferrals,
  searchValue,
  onSearchChange,
  timeframeValue,
  onTimeframeChange,
  viewType,
  onViewChange,
  onAddAdvocate,
  onAddReferral,
  noReferrals,
}: ReferralsHeaderProps) => (
  <PageHeader
    title={
      <span className="inline-flex items-center gap-2">
        Referrals
        <LoadingCount
          count={referralsCount}
          isCountLoading={isLoadingReferrals}
        />
      </span>
    }
    actions={
      <>
        {!noReferrals && (
          <>
            <SearchInput
              placeholder="Search referrals"
              value={searchValue}
              onChange={onSearchChange}
              data-cy="referrals-search-input"
            />
            <TimeframeFilter
              value={timeframeValue}
              onChange={onTimeframeChange}
            />
            {/* 32px tall, like the header's other controls. */}
            <ToggleSwitch
              value={viewType}
              onValueChange={(value) =>
                onViewChange(value as "kanban" | "table")
              }
              className="p-0.5"
            >
              <ToggleSwitchItem
                value="kanban"
                aria-label="Board view"
                className="h-7 min-w-7 px-1.5"
              >
                <SquaresFourIcon className="size-4" />
              </ToggleSwitchItem>
              <ToggleSwitchItem
                value="table"
                aria-label="Table view"
                className="h-7 min-w-7 px-1.5"
              >
                <TableIcon className="size-4" />
              </ToggleSwitchItem>
            </ToggleSwitch>
          </>
        )}
        <Button
          variant="secondary"
          onClick={onAddAdvocate}
          data-cy="add-advocate-button"
        >
          Add advocate
        </Button>
        <Button onClick={onAddReferral} data-cy="add-referral-button">
          Add referral
        </Button>
      </>
    }
  />
);
